import { useEffect, useState } from "react";
import { DeviceMobile, Share, DownloadSimple } from "@phosphor-icons/react";

// Detect if the app is already running as an installed PWA.
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIosSafari() {
  const ua = window.navigator.userAgent || "";
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  // Safari (exclude in-app browsers / Chrome-on-iOS "CriOS" / Firefox "FxiOS")
  const safari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return iOS && safari;
}

export default function InstallPrompt({ alwaysShow = false }) {
  const [canInstall, setCanInstall] = useState(!!window.__pwaPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    if (isIosSafari()) setShowIos(true);

    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const install = async () => {
    const evt = window.__pwaPrompt;
    if (!evt) return;
    evt.prompt();
    try {
      const { outcome } = await evt.userChoice;
      if (outcome === "accepted") { window.__pwaPrompt = null; setCanInstall(false); }
    } catch { /* user dismissed */ }
  };

  if (installed) {
    return alwaysShow ? (
      <div data-testid="pwa-installed-note" className="flex items-center gap-2 rounded-md border border-[#2E7D32]/30 bg-[#2E7D32]/5 px-4 py-3 text-sm font-medium text-[#2E2E2E]">
        <DeviceMobile size={18} weight="fill" className="shrink-0 text-[#2E7D32]" /> MowGuru is installed on this device.
      </div>
    ) : null;
  }

  if (canInstall) {
    return (
      <button
        type="button"
        onClick={install}
        data-testid="add-to-home-screen-button"
        className="w-full h-12 rounded-full border-2 border-[#2E7D32] text-[#2E7D32] font-semibold flex items-center justify-center gap-2 hover:bg-[#2E7D32] hover:text-white transition-colors"
      >
        <DownloadSimple size={20} weight="bold" /> Add MowGuru to Home Screen
      </button>
    );
  }

  if (showIos) {
    return (
      <div
        data-testid="ios-install-banner"
        className="flex items-start gap-2 rounded-md border border-[#2E7D32]/30 bg-[#2E7D32]/5 px-4 py-3 text-sm font-medium text-[#2E2E2E]"
      >
        <DeviceMobile size={18} weight="fill" className="mt-0.5 shrink-0 text-[#2E7D32]" />
        <span>
          Add MowGuru to your home screen: tap <Share size={15} weight="bold" className="inline align-text-bottom" /> <strong>Share</strong> → <strong>Add to Home Screen</strong>.
        </span>
      </div>
    );
  }

  // Not installable via prompt (e.g. desktop already-eligible event not fired, or unsupported browser)
  return alwaysShow ? (
    <div data-testid="pwa-install-hint" className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 px-4 py-3 text-sm font-medium text-[#2E2E2E]">
      <DeviceMobile size={18} weight="fill" className="mt-0.5 shrink-0 text-[#2E7D32]" />
      <span>To install, open your browser menu and choose <strong>Install app</strong> or <strong>Add to Home Screen</strong>.</span>
    </div>
  ) : null;
}
